import { Box, Container } from '@mui/material';
import Grid from '@mui/material/Grid';
import Head from 'next/head';
import Image from 'next/image';
import React from 'react';
import { marblebluelight } from 'src/base/Constants';
import { H1 } from 'src/base/texts/H1';
import { H2 } from 'src/base/texts/H2';
import { P } from 'src/base/texts/P';
import CallToAction from 'src/components/CallToAction';
import { FeatureStep } from 'src/components/features/FeatureStep';
import withMarketingLayout from 'src/components/layout/MarketingLayout';

const containerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  paddingTop: 10,
  maxWidth: 1000,
};

const securityImgWrapper: React.CSSProperties = {
  backgroundColor: 'white',
  border: '1px solid #d3d3d3',
  borderRadius: '20%',
  display: 'inline-flex',
  height: 80,
  width: 80,
  boxShadow: 'rgba(0, 0, 0, 0.2) 1px 2px 3px',
  justifyContent: 'center',
};

const securityImg: React.CSSProperties = {
  margin: 'auto',
  height: 40,
};

const securityItemCol: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'left',
  marginTop: 10,
  marginBottom: 10,
};

const securityItemText: React.CSSProperties = {
  display: 'inline-flex',
  marginTop: 'auto',
  marginBottom: 'auto',
  marginLeft: 20,
  fontSize: 22,
  fontWeight: 500,
  textAlign: 'left',
};

function SelfTouring(): JSX.Element {
  return (
    <>
      <Head>
        <title>Tenant Placement - Marble Virtual Rental Management</title>
        <meta
          name="description"
          content="Learn more about Marble's self-guided tenant placement system"
        />
      </Head>
      <Container style={containerStyle}>
        <Grid container>
          <Grid item md style={{ display: 'flex' }}>
            <div
              style={{
                marginTop: 'auto',
                marginBottom: 'auto',
                paddingLeft: 15,
                paddingRight: 15,
              }}
            >
              <H1>End to End Tenant Placement</H1>
              <div
                style={{
                  marginTop: 15,
                  marginBottom: 20,
                }}
              >
                <P>
                  Marble utilizes safe and secure technology for showing your
                  place autonomously. Our advanced self-touring technology
                  allows a prospective renter to tour a vacant unit without the
                  presence of an agent.
                </P>
              </div>
            </div>
          </Grid>
          <Grid item md style={{ textAlign: 'left' }}>
            <Image
              style={{ borderRadius: 5 }}
              width={450}
              height={300}
              src="/img/tour.webp"
              alt="People touring a home"
            />
          </Grid>
        </Grid>
      </Container>

      <Container style={{ marginTop: 30 }}>
        <div style={{ textAlign: 'center' }}>
          <H2>How It Works</H2>
        </div>
        <FeatureStep number="1" text="Install Marble smart lock" />
        <FeatureStep
          number="2"
          text="Marble automatically syndicates your listing to sites like Zillow, Hotpads, and Trulia"
        />
        <FeatureStep
          number="3"
          text="Prospective tenants inquire about your property and Marble automatically responds"
        />
        <FeatureStep
          number="4"
          text="Prospective tenants go through a 6-layer security check with Marble"
        />
        <FeatureStep
          number="5"
          text="Prospective tenant tours vacant property with one time access code"
        />
        <FeatureStep
          number="6"
          text="Marble automatically follows up with application and background check forms"
        />
      </Container>

      <Box
        style={{
          backgroundColor: marblebluelight,
          marginTop: 70,
          textAlign: 'center',
          paddingTop: 30,
          paddingBottom: 30,
        }}
      >
        <Container>
          <H2 style={{ fontSize: 40 }}>Proven Security</H2>
          <div style={{ marginTop: 15, marginBottom: 35 }}>
            <P align="left">
              Marble holds itself to the highest security standards with regards
              to access control and self-touring technology. We utilize a
              6-layer security system in order to ensure the security and
              protection of your property.
            </P>
          </div>
          <Grid container>
            <SecurityCheck
              img="/img/credit-card.svg"
              text="Credit Card Authentication"
            />
            <SecurityCheck
              img="/img/id-upload.svg"
              text="Government ID Upload"
            />
          </Grid>

          <Grid container>
            <SecurityCheck
              img="/img/selfie.svg"
              text="Selfie Face Verification"
            />
            <SecurityCheck img="/img/cell.svg" text="Proximity Detection" />
          </Grid>

          <Grid container>
            <SecurityCheck img="/img/ai-review.svg" text="AI Review" />
            <SecurityCheck img="/img/manual-review.svg" text="Manual Review" />
          </Grid>
        </Container>
      </Box>
      <CallToAction />
    </>
  );
}

interface SecurityCheckProps {
  img: string;
  text: string;
}

function SecurityCheck(props: SecurityCheckProps): JSX.Element {
  return (
    <>
      <style>
        {`
        @media screen and (max-width: 768px) {
          .security-item-text {
            max-width: 200px;
          }
        }
      `}
      </style>
      <Grid item style={securityItemCol} xs={12} sm={6}>
        <div style={securityImgWrapper}>
          <Image src={props.img} width={40} height={40} style={securityImg} />
        </div>
        <div style={securityItemText} className="security-item-text">
          <P>{props.text}</P>
        </div>
      </Grid>
    </>
  );
}

export default withMarketingLayout(SelfTouring);
